import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Clock, DollarSign, ChevronRight, Sparkles, Database, Tag, Trophy } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { adminApi, formatCOP } from "../../lib/api";

const STATUS_COLORS = {
  recibido: "bg-cm-yellow/30 text-cm-ink border-cm-yellow",
  en_revision: "bg-blue-100 text-blue-800 border-blue-300",
  confirmado: "bg-green-100 text-green-800 border-green-300",
  enviado: "bg-purple-100 text-purple-800 border-purple-300",
  cancelado: "bg-red-100 text-red-800 border-red-300",
};
const STATUS_LABEL = {
  recibido: "Recibido", en_revision: "En revisión", confirmado: "Confirmado",
  enviado: "Enviado", cancelado: "Cancelado",
};

const StatCard = ({ icon: Icon, label, value, sublabel, testid }) => (
  <Card className="p-5 bg-white border-cm-border shadow-none" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft font-medium">{label}</p>
        <p className="font-serif text-3xl mt-2 tracking-tight">{value}</p>
        {sublabel && <p className="text-xs text-cm-ink-soft mt-1">{sublabel}</p>}
      </div>
      <div className="h-10 w-10 rounded-md bg-cm-red/10 flex items-center justify-center">
        <Icon className="h-5 w-5 text-cm-red" />
      </div>
    </div>
  </Card>
);

const QuickLink = ({ to, icon: Icon, label, sub, testid }) => (
  <Link
    to={to}
    className="group flex items-center gap-3 p-3 rounded-md border border-cm-border bg-white hover:border-cm-red hover:bg-cm-cream-alt/40 transition-colors"
    data-testid={testid}
  >
    <div className="h-9 w-9 rounded-md bg-cm-red/10 flex items-center justify-center flex-shrink-0">
      <Icon className="h-4 w-4 text-cm-red" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-cm-ink">{label}</p>
      <p className="text-xs text-cm-ink-soft truncate">{sub}</p>
    </div>
    <ChevronRight className="h-4 w-4 text-cm-ink-soft group-hover:text-cm-red flex-shrink-0" />
  </Link>
);

export default function AdminDashboard() {
  const [m, setM] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/dashboard")
      .then((r) => setM(r.data))
      .catch(() => setM(null));
  }, []);

  if (!m) return <div className="text-cm-ink-soft" data-testid="dashboard-loading">Cargando...</div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Operación · Portal B2B</p>
        <h1 className="font-serif text-3xl sm:text-4xl mt-1 tracking-tight">Panel administrativo</h1>
        <p className="text-sm text-cm-ink-soft mt-2 max-w-2xl">
          Vista de operación del portal: pedidos por gestionar y actividad reciente. Para análisis de ventas
          consolidado (Portal + Presencial) revisá <Link to="/admin/comercial" className="text-cm-red hover:underline font-medium">Dashboard Comercial</Link>.
        </p>
      </div>

      {/* Operational KPIs only */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Package} label="Pedidos portal hoy" value={m.orders_today} sublabel={`${m.orders_total} totales`} testid="stat-orders-today" />
        <StatCard icon={Clock} label="Pendientes de gestionar" value={m.pending_orders} sublabel="Recibidos sin confirmar" testid="stat-pending" />
        <StatCard icon={DollarSign} label="Ticket promedio (7d)" value={formatCOP(m.avg_ticket)} sublabel="Solo portal" testid="stat-avg-ticket" />
      </div>

      {/* Recent orders */}
      <Card className="bg-white border-cm-border shadow-none overflow-hidden">
        <div className="px-6 py-4 border-b border-cm-border flex items-center justify-between">
          <h2 className="font-serif text-lg">Pedidos recientes (portal)</h2>
          <Link to="/admin/pedidos" className="text-xs text-cm-red hover:underline flex items-center" data-testid="view-all-orders">
            Ver todos <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
        {m.recent_orders.length === 0 ? (
          <p className="p-6 text-sm text-cm-ink-soft">Aún no hay pedidos.</p>
        ) : (
          <table className="w-full text-sm" data-testid="recent-orders-table">
            <thead className="text-left text-xs uppercase tracking-wider text-cm-ink-soft bg-cm-cream-alt/40">
              <tr><th className="px-6 py-2.5">Pedido</th><th className="px-2 py-2.5">Cliente</th><th className="px-2 py-2.5">Total</th><th className="px-6 py-2.5">Estado</th></tr>
            </thead>
            <tbody className="divide-y divide-cm-border">
              {m.recent_orders.map((o) => (
                <tr key={o.order_number} className="hover:bg-cm-cream-alt/30">
                  <td className="px-6 py-3"><Link to={`/admin/pedidos/${o.order_number}`} className="font-mono text-cm-red hover:underline">{o.order_number}</Link></td>
                  <td className="px-2 py-3"><div className="font-medium truncate max-w-[180px]">{o.razon_social}</div><div className="text-xs font-mono text-cm-ink-soft">{o.nit}</div></td>
                  <td className="px-2 py-3 font-mono font-semibold">{formatCOP(o.total)}</td>
                  <td className="px-6 py-3">
                    <Badge variant="outline" className={STATUS_COLORS[o.estado]}>{STATUS_LABEL[o.estado] || o.estado}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Quick links to other admin modules */}
      <div>
        <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium mb-3">Accesos rápidos</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="quick-links">
          <QuickLink to="/admin/comercial" icon={Sparkles} label="Dashboard Comercial" sub="Ventas, vendedores, marcas" testid="ql-comercial" />
          <QuickLink to="/admin/catalogo" icon={Database} label="Catálogo & Sync" sub="Sincronizar Arcos / Cartera" testid="ql-catalogo" />
          <QuickLink to="/admin/psm" icon={Tag} label="Precios Especiales" sub="Cargar PSM desde Excel" testid="ql-psm" />
          <QuickLink to="/admin/vendedores" icon={Trophy} label="Ranking Portal" sub="Ventas por vendedor en portal" testid="ql-ranking" />
        </div>
      </div>
    </div>
  );
}
